import setCookie from '@Utils/setCookie';

import type { AIPageControl, SortOption, ViewOption } from '@Infrastructure/entities/AIPageControl';
import type { Logger } from '@Infrastructure/entities/logger';

export interface SalesNinjaSettings {
  initUrl: string;
  fallbackUrl: string;
  id: string;
  logger: Logger;
  timeout?: number;
}

interface PersonalizationData {
  id: string;
  modifications: unknown[];
  title: string;
}

export class SalesNinja implements AIPageControl {
  private logger: Logger;

  private status: 'unavailable' | 'loading' | 'loaded';

  private resolvers: ((value?: unknown) => void)[];

  private timeout: number = null;

  private resolveAll() {
    this.resolvers.forEach((resolve) => {
      resolve();
    });

    this.resolvers = [];
  }

  private rejectAll() {
    this.resolvers.forEach((resolve) => {
      resolve(null);
    });

    this.resolvers = [];
    this.status = 'unavailable';
  }

  private preInstallScript() {
    window.ninja = function (...args: unknown[]) {
      let e: unknown;
      let r: unknown;

      const result = new Promise((resolve, reject) => {
        e = resolve;
        r = reject;
      });

      (window.ninja.r = window.ninja.r || []).push({ s: e, f: r });
      (window.ninja.c = window.ninja.c || []).push(args);

      return result;
    };

    window.SalesNinja = ['init', 'start', 'onPersonalization', 'reachGoal'].reduce(
      (acc, cur) => {
        acc[cur] = function (...args: unknown[]) {
          const arr = Array.prototype.slice.call(args);
          arr.unshift(cur);
          window.ninja(arr);
        };

        return acc;
      },
      { k: 'ninja', ready: false },
    );
  }

  private insertScript(src: string, fallbackSrc?: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = (error) => {
        reject(error);

        if (fallbackSrc?.length) this.insertScript(fallbackSrc);
      };

      document.body.appendChild(script);
    });
  }

  private runNinjaScript(id: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      window.ninja('init', id, { usePersonalizationForBadInternet: false, debug: false });

      window.ninja('start');

      const customerId = await window.ninja('getCustomerIdAsync');

      if (!customerId) reject('CustomerId not found');

      setCookie('ninja_customerId', String(customerId), { 'max-age': 60 * 60 * 24 });
      resolve(true);
    });
  }

  private initScript(settings: SalesNinjaSettings): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      const { initUrl, fallbackUrl, logger } = settings;

      try {
        await this.insertScript(initUrl, fallbackUrl);
        await this.runNinjaScript(settings.id);
        resolve(true);
      } catch (error) {
        logger.log(error);

        reject();
      }
    });
  }

  private getNinjaPersonalization(personalizationId: string): Promise<PersonalizationData> {
    return new Promise((resolve) => {
      window
        .ninja('onPersonalization', {
          personalizationId,
          handler: (_personalization, personalizationOption) => {
            resolve(personalizationOption);
          },
        })
        .catch((error) => {
          this.logger.log(error);
          resolve(null);
        });
    });
  }

  private requestTemplate(personalizationId: string): Promise<PersonalizationData> {
    return new Promise((resolve) => {
      if (!window.ninja || this.status === 'unavailable') return resolve(null);

      if (this.status === 'loading') {
        this.resolvers.push((value) => {
          if (value === null) return resolve(null);

          this.getNinjaPersonalization(personalizationId).then((res) => {
            resolve(res);
          });
        });
      } else {
        this.getNinjaPersonalization(personalizationId).then((res) => {
          resolve(res);
        });
      }
    });
  }

  private turnOffScriptByTimer() {
    setTimeout(() => {
      this.rejectAll();
    }, this.timeout);
  }

  constructor(settings: SalesNinjaSettings) {
    this.resolvers = [];
    this.status = 'unavailable';
    this.logger = settings.logger;

    if (typeof window === 'undefined') return;

    if (!window.ninja) {
      this.status = 'loading';

      if (this.timeout) {
        this.turnOffScriptByTimer();
      }

      this.preInstallScript();
      this.initScript(settings)
        .then(() => {
          this.status = 'loaded';
          this.resolveAll();
        })
        .catch(() => {
          this.rejectAll();
        });
    } else {
      this.status = 'loaded';
      this.resolveAll();
    }
  }

  async getCategorySorting(): Promise<SortOption> {
    let sort: SortOption = 'mostPopular';

    const res = await this.requestTemplate('ff4d1e53-62f5-46d4-8e3c-1c1546ee7f16');

    switch (res?.id) {
      case 'f158ea22-9423-40c9-a48f-44195e3dac9a':
        // По размеру скидки
        sort = 'discount';
        break;
      case '362fc8a1-98d7-4be3-92aa-0950923016f7':
        // Самые дорогие
        sort = 'mostExpensive';
        break;
      case '4824fbda-0b1c-4fb0-955a-c7b4f67b3ae2':
        // Самые популярные
        sort = 'mostPopular';
        break;
      case '08d4c319-33d2-4bf5-8bf7-ee8a3e125ac6':
        // Самые дешевые
        sort = 'cheapest';
        break;
    }

    return sort;
  }

  async getPageProductConstructorStructure(): Promise<ViewOption> {
    let view: ViewOption = 'default';

    const res = await this.requestTemplate('2a1bb760-5333-4fa9-b413-04d2123902fb');

    switch (res?.id) {
      case 'd85a1de6-9e0e-438f-8ca8-5a0bce8b65d4':
        view = 'view-1';
        break;
      case '0cb4f6cc-8684-4bfd-90a3-eb83adca1fa1':
        view = 'view-2';
        break;

      case 'f02a495e-f20b-41d6-a336-c19007022e4a':
      case 'cb6cd2b6-772a-44a0-8aed-eca51bca9304':
        view = 'default';
    }

    return view;
  }

  async getMainPageStructure(): Promise<ViewOption> {
    let view: ViewOption = 'default';

    const res = await this.requestTemplate('ee536ad6-5ef3-422c-91b8-ef3c635eb285');

    switch (res?.id) {
      case '41f2219b-ae1a-487c-b974-eaab26c52a40':
        view = 'view-1';
        break;
      case '420bd98b-f1c8-4d47-aea3-74ff1a1f8b94':
        view = 'default';
        break;
    }

    return view;
  }
}
